import { CodeHighlightNode, CodeNode } from '@lexical/code'
import { AutoLinkNode, LinkNode } from '@lexical/link'
import { ListItemNode, ListNode } from '@lexical/list'
import {
  $convertFromMarkdownString,
  $convertToMarkdownString,
  CHECK_LIST,
  ELEMENT_TRANSFORMERS,
  TEXT_FORMAT_TRANSFORMERS,
  TEXT_MATCH_TRANSFORMERS,
  Transformer,
} from '@lexical/markdown'
import { CheckListPlugin } from '@lexical/react/LexicalCheckListPlugin'
import { InitialConfigType, LexicalComposer } from '@lexical/react/LexicalComposer'
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import { ContentEditable } from '@lexical/react/LexicalContentEditable'
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary'
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin'
import { LinkPlugin } from '@lexical/react/LexicalLinkPlugin'
import { ListPlugin } from '@lexical/react/LexicalListPlugin'
import { MarkdownShortcutPlugin } from '@lexical/react/LexicalMarkdownShortcutPlugin'
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin'
import { HeadingNode, QuoteNode } from '@lexical/rich-text'
import { TableCellNode, TableNode, TableRowNode } from '@lexical/table'
import classNames from 'classnames'
import { $getSelection, $isRangeSelection, PASTE_COMMAND, $createTextNode, type LexicalEditor } from 'lexical'
import { ReactElement, useEffect, useMemo } from 'react'
import EditorToolbar from './EditorToolbar'
import { debug, logError } from 'lib/logging'

const DEFAULT_TRANSFORMERS: Transformer[] = [
  { format: [`underline`], tag: `____`, type: `text-format` },
  CHECK_LIST,
  ...ELEMENT_TRANSFORMERS,
  ...TEXT_FORMAT_TRANSFORMERS,
  ...TEXT_MATCH_TRANSFORMERS,
]

function AutoFocusPlugin() {
  const [editor] = useLexicalComposerContext()

  useEffect(() => {
    editor.focus()
  }, [editor])

  return null
}

function EditorHandlePlugin({ onReady }: { onReady: (editor: LexicalEditor) => void }) {
  const [editor] = useLexicalComposerContext()
  useEffect(() => {
    onReady(editor)
  }, [editor])
  return null
}

function ContentEditablePlugin({ editable }) {
  const [editor] = useLexicalComposerContext()
  useEffect(() => {
    editor.setEditable(editable)
  }, [editable])

  return (
    <ContentEditable
      className={classNames(
        `relative max-h-[70vh] overflow-y-auto text-strong outline-0`,
        editable ? `min-h-[160px] rounded-b-md border-x border-b border-default bg-surface p-4` : ``,
      )}
    />
  )
}

function OnChangePlugin({ onChange }) {
  const [editor] = useLexicalComposerContext()
  useEffect(() => {
    return editor.registerUpdateListener(({ editorState }) => {
      onChange(editorState)
    })
  }, [editor, onChange])
  return <></>
}

function Placeholder({ placeholder = `` }: { placeholder: string }) {
  return (
    <div className="pointer-events-none absolute left-4 top-4 inline-block select-none overflow-hidden text-ellipsis text-body-md text-minimal">
      {placeholder}
    </div>
  )
}

function PastePlugin() {
  const [editor] = useLexicalComposerContext()

  useEffect(() => {
    return editor.registerCommand(
      PASTE_COMMAND,
      (payload) => {
        const event = payload as ClipboardEvent
        const clipboardData = event.clipboardData || (event as any).dataTransfer

        if (clipboardData) {
          const plainText = clipboardData.getData(`text/plain`)
          if (plainText) {
            event.preventDefault()

            // Clean the text: remove multiple consecutive ~ and standalone >
            const cleanedText = plainText
              .replace(/~{2,}/g, ``) // Remove 2 or more consecutive ~ characters
              .replace(/(?<=\n|^)> (?=\n|$)/g, ``) // Remove standalone > at line start/end
              .replace(/(?<=\n|^)>$/gm, ``) // Remove standalone > at end of lines

            editor.update(() => {
              const selection = $getSelection()
              if ($isRangeSelection(selection)) {
                selection.removeText()
                const textNode = $createTextNode(cleanedText)
                selection.insertNodes([textNode])
                textNode.select()
              }
            })
            return true
          }
        }
        return false
      },
      1, // High priority
    )
  }, [editor])

  return null
}

export function RichTextEditor({
  namespace = `RichTextEditor`,
  text = ``,
  placeholder = ``,
  editable = true,
  preview = false,
  autoFocus = true,
  onChange,
  onReady,
}: {
  namespace: string
  text?: string
  placeholder?: string
  editable?: boolean
  preview?: boolean
  autoFocus?: boolean
  onChange?: (text) => void
  onReady?: (editor: LexicalEditor) => void
}): ReactElement {
  function changeHandler(editorState) {
    editorState.read(() => {
      const markdown = $convertToMarkdownString(DEFAULT_TRANSFORMERS)
      debug(`richText`, `markdown`, markdown)
      onChange?.(markdown)
    })
  }

  // Catch any errors that occur during Lexical updates and log them
  // or throw them as needed. If you don't throw them, Lexical will
  // try to recover gracefully without losing user data.
  function onError(error) {
    logError(error)
  }

  const initialConfig: InitialConfigType = useMemo(() => {
    return {
      namespace,
      editable,
      nodes: [
        HeadingNode,
        ListNode,
        ListItemNode,
        QuoteNode,
        CodeNode,
        CodeHighlightNode,
        TableNode,
        TableCellNode,
        TableRowNode,
        AutoLinkNode,
        LinkNode,
      ],
      theme: {
        list: {
          listitem: `editor-list`,
          listitemChecked: `editor-checklist-checked`,
          listitemUnchecked: `editor-checklist-unchecked`,
        },
        quote: `editor-quote`,
        text: {
          base: `flex items-center`,
          bold: `font-bold`,
          italic: `italic`,
          underline: `underline`,
          strikethrough: `line-through`,
          underlineStrikethrough: `editor-underline-strikethrough`,
        },
      },
      ...(text && {
        editorState: () => {
          let markdownText = text

          if (preview) {
            if (markdownText.length > 80 || markdownText.split(`\n`).length > 1) {
              markdownText = text.split(`\n`)[0]
              markdownText = markdownText.substring(0, 80) + ` ...`
            }
          }
          return $convertFromMarkdownString(markdownText, DEFAULT_TRANSFORMERS)
        },
      }),
      onError,
    }
  }, [editable])

  return (
    <LexicalComposer initialConfig={initialConfig}>
      <>
        {editable && <EditorToolbar />}
        <div className={`relative ${editable ? `` : ``}`}>
          <RichTextPlugin
            contentEditable={<ContentEditablePlugin editable={editable} />}
            placeholder={<Placeholder placeholder={placeholder} />}
            ErrorBoundary={LexicalErrorBoundary}
          />
          <OnChangePlugin onChange={changeHandler} />
          <HistoryPlugin />
          <ListPlugin />
          <LinkPlugin />
          <CheckListPlugin />
          <MarkdownShortcutPlugin transformers={DEFAULT_TRANSFORMERS} />
          {autoFocus && <AutoFocusPlugin />}
          {onReady && <EditorHandlePlugin onReady={onReady} />}
          <PastePlugin />
        </div>
      </>
    </LexicalComposer>
  )
}
